<template>
  <div class="setting_wrapper">
    <div class="custom-app-tabs-wrapper">
      <ul class="nav nav-tabs">
        <li
          class="nav-item"
          :class="{ active: showingActive }"
        >
          <a
            class="nav-link"
            @click="selectTab('active')"
          >
            Active
          </a>
        </li>
        <li
          class="nav-item"
          :class="{ active: !showingActive }"
        >
          <a
            class="nav-link"
            @click="selectTab('archived')"
          >
            Archived
          </a>
        </li>
      </ul>
    </div>

    <div class="d-sm-flex justify-content-between">
      <div class="mb-10">
        <h5 class="setting-title">Overtime policies</h5>
        <p class="text-muted">Set up automatic overtime policies</p>
      </div>

      <div
        v-if="!overtimePoliciesEmpty && showingActive"
        class="d-flex d-sm-block"
      >
        <AppButton
          id="new-overtime-policy"
          type="primary"
          class="flex-1"
          @click="onShowNewOvertimePolicyDialog"
        >
          Add policy
        </AppButton>
      </div>
    </div>

    <template v-if="overtimePoliciesEmpty">
      <template v-if="showingActive">
        <OvertimePoliciesNoData />
        <div class="row text-center">
          <AppButton
            id="new-overtime-policy"
            class="mt-10"
            type="primary"
            @click="onShowNewOvertimePolicyDialog"
          >
            Add policy
          </AppButton>
        </div>
      </template>
      <template v-else>
        <div class="alert alert-info h4 mt-50">No archived overtime policies</div>
      </template>
    </template>

    <template v-else>
      <AppTableLoading
        v-if="loading"
        :columns="4"
      />
      <OvertimePoliciesTable
        v-else
        :show-loading="loading"
        :pagination="pagination"
        :overtime-policies="overtime_policies"
        :showing-active="showingActive"
        :all-active-policies="all_active_policies"
        @open-edit-dialog="onShowEditOvertimePolicyDialog"
        @open-edit-members-dialog="onOpenEditMembersDialog"
        @page-changed="onFiltersChange({ page: $event })"
      />
    </template>

    <OvertimePoliciesDialog
      v-if="showNewOvertimePolicyDialog"
      id="new-overtime-policy"
      key="create-op-dialog"
      :members="members"
      :overtime-policies="overtime_policies"
      :confirmed-create="confirmedCreate"
      :all-active-policies="all_active_policies"
      :overtime-policies-url="overtimePoliciesUrl"
      @open-pay-dialog="onShowMemberPayDialog"
      @open-confirm-create-dialog="onOpenConfirmCreateDialog"
      @close="onCloseNewOvertimePolicyDialog"
    />

    <OvertimePoliciesDialog
      v-if="showEditOvertimePolicyDialog"
      id="edit-overtime-policy"
      key="edit-op-dialog"
      :overtime-policy="overtimePolicy"
      :overtime-policies="overtime_policies"
      :members="members"
      :all-active-policies="all_active_policies"
      :overtime-policies-url="overtimePoliciesUrl"
      editing
      @open-pay-dialog="onShowMemberPayDialog"
      @close="onCloseEditOvertimePolicyDialog"
    />

    <OvertimePoliciesMembersDialog
      v-if="showEditMembersDialog"
      id="edit-overtime-members"
      key="edit-op-members-dialog"
      :overtime-policy="overtimePolicy"
      :overtime-policies="overtime_policies"
      :members="members"
      :all-active-policies="all_active_policies"
      :overtime-policies-url="overtimePoliciesUrl"
      @open-pay-dialog="onShowMemberPayDialog"
      @close="onCloseOvertimeMembersDialog"
    />

    <OvertimePoliciesMemberPayDialog
      v-if="showMemberPayDialog"
      :member="member"
      @close="onCloseMemberPayDialog"
    />

    <OvertimePoliciesConfirmCreateDialog
      v-if="showConfirmCreateDialog"
      key="confirm-create-dialog"
      @close="onCloseConfirmCreateDialog"
      @close-confirmed="onConfirmCreate"
    />
  </div>
</template>

<script>
// Inertia helpers/utils
import { InertiaFlashWatcher, InertiaLoadingWatcher } from '~/utils/inertia';
// Components
import OvertimePoliciesNoData from '~/components/overtime_policies/OvertimePoliciesNoData.vue';
import OvertimePoliciesTable from '~/components/overtime_policies/OvertimePoliciesTable.vue';
import OvertimePoliciesDialog from '~/components/overtime_policies/OvertimePoliciesDialog.vue';
import OvertimePoliciesMembersDialog from '~/components/overtime_policies/OvertimePoliciesMembersDialog.vue';
import OvertimePoliciesMemberPayDialog from '~/components/overtime_policies/OvertimePoliciesMemberPayDialog.vue';
import OvertimePoliciesConfirmCreateDialog from '~/components/overtime_policies/OvertimePoliciesConfirmCreateDialog.vue';

export default {
  name: 'OvertimePolicies',
  components: {
    OvertimePoliciesNoData,
    OvertimePoliciesTable,
    OvertimePoliciesDialog,
    OvertimePoliciesMembersDialog,
    OvertimePoliciesMemberPayDialog,
    OvertimePoliciesConfirmCreateDialog,
  },
  mixins: [InertiaFlashWatcher, InertiaLoadingWatcher],
  props: {
    all_active_policies: {
      type: Array,
      required: true,
    },
    members: {
      type: Array,
      required: true,
    },
    organization: {
      type: Object,
      required: true,
    },
    overtime_policies: {
      type: Array,
      required: true,
    },
    overtime_policies_query: {
      type: Object,
      required: true,
    },
    pagination: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      overtimePolicy: null,
      showNewOvertimePolicyDialog: false,
      showEditOvertimePolicyDialog: false,
      showEditMembersDialog: false,
      showMemberPayDialog: false,
      showConfirmCreateDialog: false,
      member: {},
      confirmedCreate: false,
      selectedTab: 'active',
    };
  },
  computed: {
    overtimePoliciesEmpty() {
      return (this.overtime_policies || []).length === 0;
    },
    showingActive() {
      return this.overtime_policies_query.status !== 'archived';
    },
    currentParams() {
      return Object.entries(this.overtime_policies_query).filter(([, value]) => !!value);
    },
    overtimePoliciesUrl() {
      return [
        '/overtime_policies/:overtimePolicyId',
        this.showingActive ? '' : `?status=${this.overtime_policies_query.status}`,
      ].join('');
    },
  },
  methods: {
    onFiltersChange(filters = {}) {
      const { origin, pathname } = window.location;
      const url = new URL(pathname, origin);
      const search = new URLSearchParams(this.currentParams);

      Object.entries(filters).forEach(([filterKey, filter]) => {
        if (filter) {
          search.set(filterKey, filter);
        } else {
          search.delete(filterKey);
        }
      });
      url.search = search.toString();

      this.$inertia.visit(url.toString());
    },
    onShowNewOvertimePolicyDialog() {
      this.showNewOvertimePolicyDialog = true;
    },
    onShowEditOvertimePolicyDialog(policy) {
      this.overtimePolicy = policy;
      this.showEditOvertimePolicyDialog = true;
    },
    onShowMemberPayDialog(member) {
      this.member = member;
      this.showMemberPayDialog = true;
    },
    onCloseNewOvertimePolicyDialog() {
      this.confirmedCreate = false;
      this.showNewOvertimePolicyDialog = false;
    },
    onCloseEditOvertimePolicyDialog() {
      this.overtimePolicy = {};
      this.showEditOvertimePolicyDialog = false;
    },
    onOpenEditMembersDialog(policy) {
      this.overtimePolicy = policy;
      this.showEditMembersDialog = true;
    },
    onOpenConfirmCreateDialog() {
      this.showConfirmCreateDialog = true;
    },
    onCloseOvertimeMembersDialog() {
      this.overtimePolicy = {};
      this.showEditMembersDialog = false;
    },
    onCloseMemberPayDialog() {
      this.member = {};
      this.showMemberPayDialog = false;
    },
    onCloseConfirmCreateDialog() {
      this.showConfirmCreateDialog = false;
    },
    onConfirmCreate() {
      this.showConfirmCreateDialog = false;
      this.confirmedCreate = true;
    },
    selectTab(status) {
      this.onFiltersChange({ status, page: 0 });
    },
  },
};
</script>
